/* eslint-disable @typescript-eslint/no-explicit-any */
import { CancelablePromise } from '@talon/web/lib/api';
import { OpenAPI } from '@talon/web/lib/api/core/OpenAPI';
import { config } from '@talon/web/src/config';
import { makeObservable, observable } from 'mobx';
import { toast } from 'react-toastify';
import { reportError } from '../../utils/errorReporting';
import {
  AccountStore,
  AdminStore,
  ChatStore,
  CommercialStore,
  CostumeStore,
  EstimateStore,
  HoldingFeeStore,
  NotificationStore,
  PerformerStore,
  ProjectStore,
  PropStore,
  RightStore,
  RoleStore,
  SessionActivityStore,
  SessionStore,
  TalentManagerStore,
  UsageStore,
  UserStore,
} from '.';

export class RootStore {
  accountStore: AccountStore;
  adminStore: AdminStore;
  chatStore: ChatStore;
  commercialStore: CommercialStore;
  costumeStore: CostumeStore;
  estimateStore: EstimateStore;
  holdingFeeStore: HoldingFeeStore;
  notificationStore: NotificationStore;
  performerStore: PerformerStore;
  projectStore: ProjectStore;
  propStore: PropStore;
  rightStore: RightStore;
  roleStore: RoleStore;
  sessionActivityStore: SessionActivityStore;
  sessionStore: SessionStore;
  talentManagerStore: TalentManagerStore;
  usageStore: UsageStore;
  userStore: UserStore;
  requests: CancelablePromise<any>[] = [];

  constructor() {
    OpenAPI.BASE = `${config.env.apiHost}:${config.env.apiPort}/${config.env.apiBase}/${config.env.apiVersion}`;
    this.accountStore = new AccountStore(this);
    this.adminStore = new AdminStore(this);
    this.chatStore = new ChatStore(this);
    this.commercialStore = new CommercialStore(this);
    this.costumeStore = new CostumeStore(this);
    this.estimateStore = new EstimateStore(this);
    this.holdingFeeStore = new HoldingFeeStore(this);
    this.notificationStore = new NotificationStore(this);
    this.performerStore = new PerformerStore(this);
    this.projectStore = new ProjectStore(this);
    this.propStore = new PropStore(this);
    this.rightStore = new RightStore(this);
    this.roleStore = new RoleStore(this);
    this.sessionActivityStore = new SessionActivityStore(this);
    this.sessionStore = new SessionStore(this);
    this.talentManagerStore = new TalentManagerStore(this);
    this.usageStore = new UsageStore(this);
    this.userStore = new UserStore(this);

    makeObservable(this, {
      accountStore: observable,
      adminStore: observable,
      chatStore: observable,
      commercialStore: observable,
      costumeStore: observable,
      estimateStore: observable,
      holdingFeeStore: observable,
      notificationStore: observable,
      performerStore: observable,
      projectStore: observable,
      propStore: observable,
      rightStore: observable,
      roleStore: observable,
      sessionActivityStore: observable,
      sessionStore: observable,
      talentManagerStore: observable,
      usageStore: observable,
      userStore: observable,
    });
  }
  handleError = (error: any, customMsg?: string) => {
    // console.log('error: ', JSON.stringify(error, null, 2));
    // `error` here is the generated client's `ApiError` (`.status`/`.message`), not an axios
    // error — there is no `.response`, so this branch never matched and every 401 fell through
    // to the generic path below, showing the raw "Unauthorized" status text once per failed
    // request. A fixed `toastId` collapses repeats (e.g. several components' calls all 401ing
    // in the same burst) into a single toast instead of stacking one per call.
    if (error?.status === 401) {
      this.cancelRequests();
      this.clearRequests();
      toast.error(customMsg || 'Your session has expired. Please log in again', { toastId: 'session-expired' });
      return;
    }
    console.error(error);
    const ref = reportError(error);
    const message = customMsg || error.response?.data?.message || error.message || 'An error occurred';
    // A server-error reference lets whoever sees the toast point us at the exact API log line
    toast.error(ref ? `${message} (ref: ${ref})` : message);
  };
  cancelRequests = () => {
    this.requests.forEach((request) => request.cancel());
    this.clearRequests();
  };
  cancelRequest = (request: CancelablePromise<any>) => {
    request.cancel();
    this.removeRequest(request);
  };
  addRequest = async <T>(request: CancelablePromise<T>): Promise<T> => {
    this.requests.push(request);
    return await request
      .then((res) => {
        this.removeRequest(request);
        return res;
      })
      .catch((e) => {
        this.removeRequest(request);
        throw e;
      });
  };
  removeRequest = (request: CancelablePromise<any>) => {
    this.requests = this.requests.filter((req) => req !== request);
  };
  clearRequests = () => {
    this.requests = [];
  };
  get isRequesting() {
    return this.requests.length > 0;
  }
  get hasRequests() {
    return this.requests.length > 0;
  }
  get hasNoRequests() {
    return this.requests.length === 0;
  }
}
